import {
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import {
  Module,
  PATH_PROJECT_CONTRACTOR_VISITS_EDITOR_DIRECTORY,
  PATH_PROJECT_DEPARTMENT_BASED_ASSIGNMENTS,
  PATH_PROJECT_LIMITS,
  PATH_PROJECT_RATES,
  PATH_PROJECT_ROLE_BASED_ASSIGNMENTS,
  PATH_PROJECT_ROLES,
  PATH_PROJECT_SCHEDULES,
  PATH_PROJECT_STORES,
} from "@/constants";
import { usePermissions } from "@/modules/account/hooks/usePermissions";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";
import { ReadProject } from "@/types/project";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

interface ProjectStoreSubMenuProps {
  projectId: string;
  project?: ReadProject;
}

const ProjectStoreSubMenuComponent = ({
  projectId,
  project,
}: ProjectStoreSubMenuProps) => {
  const { t } = useTranslation();
  const options = useMemo(() => ({ projectId }), [projectId]);
  const isRoleDependent = isProjectRoleDependent(project);
  const permissions = usePermissions();

  return (
    <MenubarMenu>
      <MenubarTrigger>{t("projects.menubar.store.trigger")}</MenubarTrigger>
      <MenubarContent>
        {permissions[Module.ProjectStores].can_read && (
          <MenubarItem asChild>
            <Link to={generatePath(PATH_PROJECT_STORES, options)}>
              {t("projects.menubar.store.assignStores")}
            </Link>
          </MenubarItem>
        )}

        {isRoleDependent && permissions[Module.ProjectRoles].can_read && (
          <MenubarItem asChild>
            <Link to={generatePath(PATH_PROJECT_ROLES, options)}>
              {t("projects.menubar.store.assignRoles")}
            </Link>
          </MenubarItem>
        )}

        {permissions[Module.ProjectLimits].can_read && (
          <MenubarItem asChild>
            <Link to={generatePath(PATH_PROJECT_LIMITS, options)}>
              {t("projects.menubar.store.assignLimits")}
            </Link>
          </MenubarItem>
        )}

        {isRoleDependent &&
          permissions[Module.RoleBasedProjectRates].can_read && (
            <MenubarItem asChild>
              <Link to={generatePath(PATH_PROJECT_RATES, options)}>
                {t("projects.menubar.store.setupRates")}
              </Link>
            </MenubarItem>
          )}

        {isRoleDependent && permissions[Module.ProjectSchedules].can_read && (
          <MenubarItem asChild>
            <Link to={generatePath(PATH_PROJECT_SCHEDULES, options)}>
              {t("projects.menubar.store.setupSchedules")}
            </Link>
          </MenubarItem>
        )}

        {!isRoleDependent &&
          permissions[Module.DepartmentBasedAssignments].can_read && (
            <MenubarItem asChild>
              <Link
                to={generatePath(
                  PATH_PROJECT_DEPARTMENT_BASED_ASSIGNMENTS,
                  options,
                )}
              >
                {t("projects.menubar.store.assignContractors")}
              </Link>
            </MenubarItem>
          )}

        {isRoleDependent && (
          <>
            {permissions[Module.RoleBasedAssignments].can_read && (
              <MenubarItem asChild>
                <Link
                  to={generatePath(
                    PATH_PROJECT_ROLE_BASED_ASSIGNMENTS,
                    options,
                  )}
                >
                  {t("projects.menubar.store.assignContractors")}
                </Link>
              </MenubarItem>
            )}

            {permissions[Module.ContractorVisits].can_read && (
              <MenubarItem asChild>
                <Link
                  to={generatePath(
                    PATH_PROJECT_CONTRACTOR_VISITS_EDITOR_DIRECTORY,
                    options,
                  )}
                >
                  {t("projects.menubar.store.editVisits")}
                </Link>
              </MenubarItem>
            )}
          </>
        )}
      </MenubarContent>
    </MenubarMenu>
  );
};

export const ProjectStoreSubMenu = memo(ProjectStoreSubMenuComponent);
